import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { Modal } from '../components/ui/SimpleModal';
import { CustomerValidationsPanel } from '../components/CustomerValidationsPanel';
import { FEATURES } from '../config/features';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  UNDER_REVIEW: 'En revisión',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CONVERTED: 'Convertida',
  CANCELLED: 'Cancelada',
};

function apiErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string') return msg;
  return err?.message ?? 'Error desconocido';
}

function actorLabel(u?: { firstName?: string; lastName?: string; email?: string } | null) {
  if (!u) return '—';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.email || '—';
}

/** CBU argentino: 22 dígitos. */
function hasValidCustomerCbu(customer?: { cbu?: string | null } | null): boolean {
  const digits = String(customer?.cbu ?? '').replace(/\D/g, '');
  return digits.length === 22;
}

export function LoanApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const apps = await apiService.listLoanApplications(
        statusFilter === 'ALL' ? 'ALL' : statusFilter || undefined,
      );
      setApplications(apps);
    } catch (e: any) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
    setDetailLoading(true);
    setError(null);
    try {
      const app = await apiService.getLoanApplication(id);
      setSelected(app);
    } catch (e: any) {
      setError(apiErrorMessage(e));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
    setSelected(null);
  };

  const refreshDetail = async (id: string) => {
    try {
      const app = await apiService.getLoanApplication(id);
      setSelected(app);
    } catch {
      closeDetail();
    }
  };

  const review = async (id: string, status: string, override?: { validationOverride?: boolean; validationOverrideReason?: string }) => {
    setActingOn(id);
    setError(null);
    try {
      await apiService.reviewLoanApplication(id, { status, ...override });
      await load();
      if (detailOpen && selectedId === id) {
        await refreshDetail(id);
      }
    } catch (e: any) {
      const data = e?.response?.data;
      if (status === 'APPROVED' && e?.response?.status === 409 && data?.requiresOverride) {
        const reason = window.prompt(
          `${data.message ?? 'Hay controles en rojo.'}\n\nMotivo para continuar (mín. 10 caracteres):`,
        );
        if (reason && reason.trim().length >= 10) {
          await review(id, status, { validationOverride: true, validationOverrideReason: reason.trim() });
          return;
        }
      }
      setError(apiErrorMessage(e));
    } finally {
      setActingOn(null);
    }
  };

  const convert = async (
    id: string,
    override?: { validationOverride?: boolean; validationOverrideReason?: string },
  ) => {
    setActingOn(id);
    setError(null);
    try {
      const startDate =
        selectedId === id && selected?.requestedStartDate
          ? String(selected.requestedStartDate).slice(0, 10)
          : undefined;
      await apiService.convertLoanApplication(id, {
        ...(startDate ? { startDate } : {}),
        ...(override ?? {}),
      });
      await load();
      if (detailOpen && selectedId === id) {
        await refreshDetail(id);
      }
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.requiresOverride) {
        const reason = window.prompt(
          `${data.message ?? 'Hay controles en rojo.'}\n\nMotivo para continuar (mín. 10 caracteres):`,
        );
        if (reason && reason.trim().length >= 10) {
          await convert(id, { validationOverride: true, validationOverrideReason: reason.trim() });
          return;
        }
      }
      setError(apiErrorMessage(e));
    } finally {
      setActingOn(null);
    }
  };

  const canAct = (status: string) =>
    ['PENDING', 'UNDER_REVIEW', 'APPROVED'].includes(status);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Presolicitudes</h1>
          <p className="text-sm text-gray-500 mt-1">Pipeline comercial — captura y conversión a préstamo</p>
        </div>
        <button
          onClick={() => navigate('/applications/new')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          + Nueva presolicitud
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-gray-600">Filtrar estado:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
        >
          <option value="">En curso / rechazadas</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
          <option value="ALL">Todas (incluye convertidas)</option>
        </select>
        <button onClick={load} className="text-sm text-blue-600 hover:underline">Actualizar</button>
      </div>

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : applications.length === 0 ? (
        <p className="text-gray-500">No hay presolicitudes.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {applications.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetail(a.id)}
                >
                  <td className="px-4 py-3 text-sm">{a.documentNumber}</td>
                  <td className="px-4 py-3 text-sm">
                    {a.firstName} {a.lastName}
                    {a.customer && <span className="text-gray-400 text-xs block">{a.customer.email}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">${a.requestedAmount?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">{STATUS_LABELS[a.status] ?? a.status}</td>
                  <td className="px-4 py-3 text-sm">{a.branch?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetail(a.id);
                      }}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      Ver / Revisar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={detailOpen}
        onClose={closeDetail}
        title="Detalle de presolicitud"
        size="lg"
      >
        {detailLoading ? (
          <p className="text-gray-500 py-8 text-center">Cargando…</p>
        ) : selected ? (
          <div className="space-y-6">
            {error && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Documento</p>
                <p className="font-medium">{selected.documentNumber}</p>
              </div>
              <div>
                <p className="text-gray-500">Estado</p>
                <p className="font-medium">{STATUS_LABELS[selected.status] ?? selected.status}</p>
              </div>
              <div>
                <p className="text-gray-500">Cliente</p>
                <p className="font-medium">
                  {selected.firstName} {selected.lastName}
                </p>
                {selected.customer?.email && (
                  <p className="text-gray-500 text-xs">{selected.customer.email}</p>
                )}
              </div>
              <div>
                <p className="text-gray-500">Teléfono</p>
                <p className="font-medium">{selected.phone ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Monto solicitado</p>
                <p className="font-medium">${selected.requestedAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Plazo</p>
                <p className="font-medium">{selected.requestedTermMonths ?? '—'} meses</p>
              </div>
              <div>
                <p className="text-gray-500">Fecha inicio / otorgamiento</p>
                <p className="font-medium">
                  {selected.requestedStartDate
                    ? new Date(selected.requestedStartDate).toLocaleDateString('es-AR')
                    : '— (hoy al convertir)'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Sucursal</p>
                <p className="font-medium">{selected.branch?.name ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Producto</p>
                <p className="font-medium">
                  {selected.product?.name ?? '—'}
                  {selected.product?.planCode ? ` (${selected.product.planCode})` : ''}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Origen</p>
                <p className="font-medium">{selected.referralSource?.name ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Creada</p>
                <p className="font-medium">
                  {selected.createdAt ? new Date(selected.createdAt).toLocaleString('es-AR') : '—'}
                </p>
              </div>
              {selected.notes && (
                <div className="col-span-2">
                  <p className="text-gray-500">Notas</p>
                  <p className="font-medium">{selected.notes}</p>
                </div>
              )}
              {selected.status === 'CONVERTED' && selected.convertedLoan && (
                <div className="col-span-2">
                  <p className="text-gray-500">Préstamo generado</p>
                  <p className="font-medium">
                    #{selected.convertedLoan.loanNumber ?? selected.convertedLoan.id.slice(0, 8)}
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase text-gray-500">Operadores</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Creó</p>
                  <p className="font-medium">{actorLabel(selected.createdBy)}</p>
                  <p className="text-xs text-gray-500">
                    {selected.createdAt ? new Date(selected.createdAt).toLocaleString('es-AR') : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Revisó</p>
                  <p className="font-medium">{actorLabel(selected.reviewedBy)}</p>
                  <p className="text-xs text-gray-500">
                    {selected.reviewedAt ? new Date(selected.reviewedAt).toLocaleString('es-AR') : '—'}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <CustomerValidationsPanel
                customerId={selected.customerId || undefined}
                applicationId={selected.customerId ? undefined : selected.id}
              />
            </div>

            {canAct(selected.status) && (
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                {FEATURES.kyc && selected.customerId && selected.customer?.kycStatus !== 'APPROVED' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-2 w-full">
                    KYC del cliente: <strong>{selected.customer?.kycStatus ?? 'PENDING'}</strong>.
                    Debe estar aprobado antes de convertir a préstamo (módulo KYC).
                  </p>
                )}
                {!selected.customerId && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-md p-2 w-full">
                    Sin cliente vinculado: al convertir se creará automáticamente con el documento{' '}
                    {selected.documentNumber}.
                  </p>
                )}
                {(selected.status === 'PENDING' || selected.status === 'UNDER_REVIEW') &&
                  selected.customerId &&
                  !hasValidCustomerCbu(selected.customer) && (
                    <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2 w-full">
                      <strong>Falta CBU del cliente.</strong> Registralo en la ficha del cliente antes de
                      aprobar si el desembolso o débitos BAPRO lo requieren.
                      {selected.customerId ? (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="underline font-medium"
                            onClick={() => navigate(`/customers/${selected.customerId}`)}
                          >
                            Ir a la ficha
                          </button>
                        </>
                      ) : null}
                    </p>
                  )}
                {selected.status === 'PENDING' && (
                  <button
                    type="button"
                    disabled={actingOn === selected.id}
                    onClick={() => review(selected.id, 'UNDER_REVIEW')}
                    className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 disabled:opacity-50"
                  >
                    Marcar en revisión
                  </button>
                )}
                {(selected.status === 'PENDING' || selected.status === 'UNDER_REVIEW') && (
                  <button
                    type="button"
                    disabled={actingOn === selected.id}
                    onClick={() => {
                      if (
                        selected.customerId &&
                        !hasValidCustomerCbu(selected.customer) &&
                        !window.confirm(
                          'El cliente no tiene CBU cargado (22 dígitos).\n\n¿Seguro que querés aprobar la presolicitud igual?',
                        )
                      ) {
                        return;
                      }
                      void review(selected.id, 'APPROVED');
                    }}
                    className="px-3 py-2 text-sm bg-green-50 text-green-700 rounded-md hover:bg-green-100 disabled:opacity-50"
                  >
                    Aprobar
                  </button>
                )}
                {(selected.status === 'PENDING' ||
                  selected.status === 'UNDER_REVIEW' ||
                  selected.status === 'APPROVED') && (
                  <button
                    type="button"
                    disabled={actingOn === selected.id}
                    onClick={() => review(selected.id, 'REJECTED')}
                    className="px-3 py-2 text-sm bg-red-50 text-red-700 rounded-md hover:bg-red-100 disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                )}
                <button
                  type="button"
                  disabled={actingOn === selected.id}
                  onClick={() => convert(selected.id)}
                  className="px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                >
                  Convertir a préstamo
                </button>
              </div>
            )}

            {statusFilter && selected.status !== statusFilter && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-2">
                Esta solicitud ya no coincide con el filtro «{STATUS_LABELS[statusFilter]}». Cambiá el filtro a «Todos» para verla en la lista.
              </p>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
